//Handle debt service ratio calculates and rating.
export default class {
  constructor(
    //settings
    { dsrRatings = [], networthRatings = [], dsrMethodSettings = {} },

    //totals
    {
      totalLiabilities,
      totalAssets,
      totalExpenses,
      totalIncome,
      totalDebts,
      totalStatutoryDeductions,
      totalNonStatutoryDeductions
    }
  ) {
    this.networthRatings = networthRatings;
    this.dsrRatings = dsrRatings;

    this.totalLiabilities = totalLiabilities || 0;
    this.totalAssets = totalAssets || 0;
    this.totalExpenses = totalExpenses || 0;
    this.totalIncome = totalIncome || 0;
    this.totalDebts = totalDebts || 0;
    this.totalStatutoryDeductions = totalStatutoryDeductions || 0;
    this.totalNonStatutoryDeductions = totalNonStatutoryDeductions || 0;

    this.formula = dsrMethodSettings?.formula;
    this.isCreditScore = dsrMethodSettings?.isCreditScore || false;
    this.ratioName = dsrMethodSettings?.ratioName || '-';
    this.isStopperActive = dsrMethodSettings?.isStopperActive || false;
    this.stopperLimit = +dsrMethodSettings?.stopperLimit || 0;
    this.expenseDefinition = dsrMethodSettings?.expenseDefinition;
    this.netIncomeDefinition = dsrMethodSettings?.incomeDefinition;
    this.expenseDefinitionIndex =
      dsrMethodSettings?.expenseDefinition?.includes('Non') ||
      dsrMethodSettings?.expenseDefinition?.includes('ns')
        ? 0
        : 1;
    this.netIncomeDefinitionIndex =
      dsrMethodSettings?.incomeDefinition?.includes('Non') ||
      dsrMethodSettings?.incomeDefinition?.includes('ns')
        ? 0
        : 1;
    this.isDSRCategory = dsrMethodSettings?.ratioCategory?.includes('DSR');
    this.ratioCategory = dsrMethodSettings?.ratioCategory;
  }

  //Calculate and return the results and supplimentary info for the calculated dsr.
  getDSRResults() {
    const ratio = this.calculateDSRRatio();
    const rating = this.findDSRRatings(ratio);
    return {
      ratio,
      rating,
      formula: this.formula,
      ratioCategory: this.ratioCategory,
      ratingColor: this.getDSRRatingColor(rating),
      ratioName: this.ratioName,
      stopperLimit: this.stopperLimit,
      stopFlow:
        this.isStopperActive &&
        this.stopperLimit != 0 &&
        this.stopperLimit < ratio,
      isCreditScore: this.isCreditScore
    };
  }

  //Calculate and return the results and supplimentary info for the calculated dsr.
  getNetworthResults() {
    const results = this.calculateNetRatio();
    const rating = this.findNetRating(results.netRatio);
    return {
      ...results,
      rating,
      ratingColor: this.getNetRatingColor(rating)
    };
  }

  //Calculate the dsr ratio
  calculateNetRatio() {
    const networth = this.totalAssets - this.totalLiabilities;

    const net = (this.totalAssets / (this.totalLiabilities || 1)) * 100;
    const netRatio = +(net > 100 ? 100 : net).toFixed(2);
    const netRating = this.findNetRating(netRatio);
    const netRatingColor = this.getNetRatingColor(netRating);

    return {
      totalAssets: this.totalAssets,
      totalLiabilities: this.totalLiabilities,
      networth,
      netRatio,
      netRating,
      netRatingColor
    };
  }

  //Calculate the dsr ratio
  calculateDSRRatio() {
    const totalIncome = this.calculateIncome();
    if (totalIncome <= 0) return 0;
    const result = (this.calculateDebtsAndExpenses() / totalIncome) * 100;
    const { dsrMaxScore, ieMaxScore } = this.dsrRatings[0];
    const score = Number(this.isDSRCategory ? dsrMaxScore : ieMaxScore);

    const ratio = result < 0 ? score : result;

    return +ratio.toFixed(2);
  }

  //Calculate the gross or net income according to the formula
  //and net income definition
  calculateIncome() {
    if (
      this.formula.includes('Gross') ||
      this.netIncomeDefinition === 'np = gp'
    )
      return this.totalIncome;

    if (this.netIncomeDefinitionIndex == 1)
      return this.totalIncome - this.totalStatutoryDeductions;

    return (
      this.totalIncome -
      (+this.totalNonStatutoryDeductions + this.totalStatutoryDeductions)
    );
  }

  //Calculate the total payment of debt and expenses
  //based on the formula and expense definition.
  calculateDebtsAndExpenses() {
    if (!this.formula.includes('Expense')) return this.totalDebts;

    if (this.expenseDefinition === 'e = (sd + ns + de)')
      return (
        +this.totalExpenses +
        this.totalDebts +
        this.totalNonStatutoryDeductions +
        this.totalStatutoryDeductions
      );

    if (this.expenseDefinition === 'e = (sd + de)')
      return (
        +this.totalExpenses + this.totalDebts + this.totalStatutoryDeductions
      );

    if (this.expenseDefinitionIndex == 1)
      return +this.totalExpenses + this.totalDebts;

    return (
      this.totalDebts + this.totalExpenses + this.totalNonStatutoryDeductions
    );
  }

  //Find and return the rating level given a dsr ratio
  //Levels can be either "Low", :Average", or "High""
  findDSRRatings(ratio) {
    let rating;
    let i = 0;
    let len = this.dsrRatings.length - 1;

    const ratings = this.dsrRatings.sort((a, b) => {
      const aDsrScore = Number(
        this.isDSRCategory ? a.dsrMaxScore : a.ieMaxScore
      );
      const bDsrScore = Number(
        this.isDSRCategory ? b.dsrMaxScore : b.ieMaxScore
      );

      return bDsrScore - aDsrScore;
    });

    while (i <= len) {
      const { ratingName, dsrMaxScore, ieMaxScore } = ratings[i];
      const dsrScore = Number(this.isDSRCategory ? dsrMaxScore : ieMaxScore);

      if (i == 0) {
        i++;
        if (ratio >= dsrScore) {
          rating = ratingName;
          break;
        }
        continue;
      }

      if (i == len) {
        i++;
        if (ratio <= dsrScore) {
          rating = ratingName;
          break;
        }
        continue;
      }

      rating = ratingName;
      i++;
    }

    return rating;
  }

  //Return the color corresponding to the rating level for dsr
  getDSRRatingColor(rating) {
    const ratingColorMap = {
      high: 'red',
      average: 'orange',
      low: 'blue'
    };

    return ratingColorMap[rating?.toLowerCase()];
  }

  //Find and return the rating level given a net ratio
  //Levels can be either "Low", :Average", or "High""
  findNetRating(ratio) {
    let rating;
    let i = 0;
    let len = this.networthRatings.length - 1;

    const ratings = this.networthRatings.sort((a, b) => {
      const aScore = +a.maxScore;
      const bScore = +b.maxScore;
      return bScore - aScore;
    });

    while (i <= len) {
      const { ratingName, maxScore } = ratings[i];

      if (i == 0) {
        i++;
        if (ratio >= maxScore) {
          rating = ratingName;
          break;
        }
        continue;
      }

      if (i == len) {
        i++;
        if (ratio <= maxScore) {
          rating = ratingName;
          break;
        }
        continue;
      }

      rating = ratingName;
      i++;
    }
    return rating;
  }

  //Return the color corresponding to the rating level for networth
  getNetRatingColor(rating) {
    const ratingColorMap = {
      low: 'blue',
      average: 'orange',
      high: 'red'
    };
    return ratingColorMap[rating?.toLowerCase()];
  }
}
